'use client'

import { create } from 'zustand';

export interface InquiryItem {
  product_id: number;
  product_name: string;
  quantity: number;
  unit: 'g' | 'kg';
  price_per_unit: number;
  // Store full product data for metadata
  product_data?: any;
}

export interface InquiryStore {
  selectedItems: InquiryItem[];
  addItem: (product: any, quantity?: number, unit?: 'g' | 'kg') => void;
  removeItem: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clearItems: () => void;
  getTotalItems: () => number;
}

export const useInquiryStore = create<InquiryStore>((set, get) => ({
  selectedItems: [],

  addItem: (product: any, quantity: number = 100, unit: 'g' | 'kg' = 'g') => {
    const { selectedItems } = get();
    
    // Convert quantity to base unit (grams) for consistency
    const baseQuantity = unit === 'kg' ? quantity * 1000 : quantity;
    
    // Check if item already exists
    const existingItem = selectedItems.find(item => item.product_id === product.id);

    if (existingItem) {
      // Update existing item quantity
      const newQuantity = existingItem.quantity + baseQuantity;
      const updatedItems = selectedItems.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: newQuantity }
          : item
      );
      
      set({ selectedItems: updatedItems });
    } else {
      // Add new item
      const newItem: InquiryItem = {
        product_id: product.id,
        product_name: product.name,
        quantity: baseQuantity,
        unit: 'g', // Always store in grams for consistency
        price_per_unit: parseFloat(product.price || '0'),
        product_data: product, // Store full product data for metadata
      };

      set({ selectedItems: [...selectedItems, newItem] });
    }
  },

  removeItem: (productId: number) => {
    const { selectedItems } = get();
    const updatedItems = selectedItems.filter(item => item.product_id !== productId);
    set({ selectedItems: updatedItems });
  },

  updateQuantity: (productId: number, quantity: number) => {
    const { selectedItems } = get();
    
    if (quantity <= 0) {
      // Remove item if quantity is 0 or negative
      get().removeItem(productId);
      return;
    }

    const updatedItems = selectedItems.map(item =>
      item.product_id === productId
        ? { ...item, quantity }
        : item
    );

    set({ selectedItems: updatedItems });
  },

  clearItems: () => {
    set({ selectedItems: [] });
  },

  getTotalItems: () => {
    const { selectedItems } = get();
    return selectedItems.reduce((total, item) => total + item.quantity, 0);
  },
}));